// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { InternalProposalOperation } from '@shared/data-access/interfaces/api/brokerage/proposals-types';
import {
  CASH_ACCOUNT_IBAN,
  NUVARA_INVERSIS_INSTITUTION_ID,
  STATIC_INVERSIS_CUSTODY_IBAN,
} from '@shared/data-access/utils/nuvara-inversis-institution';
import { isCashIdentifier } from './map-target-portfolio-request';

/** IBAN values that stand for a perimeter, not for a real client account. */
const PLACEHOLDER_IBANS = new Set<string>([
  CASH_ACCOUNT_IBAN,
  NUVARA_INVERSIS_INSTITUTION_ID,
  STATIC_INVERSIS_CUSTODY_IBAN,
]);

/** Asset an operation takes out of an account, as the operation identifies it. */
export interface OperationSourceAsset {
  readonly ticker: string;
  readonly isin: string | null;
  /** Identifiers the matching position may be known by. */
  readonly identifiers: ReadonlySet<string>;
}

/** Source assets the operations need, grouped by the IBAN that holds them. */
export type OperationSourceKeys = ReadonlyMap<
  string,
  ReadonlyArray<OperationSourceAsset>
>;

/** A position-like record that can be matched against {@link OperationSourceKeys}. */
export interface OperationSourceCandidate {
  readonly iban?: string | null;
  /** Every identifier the position is known by (ticker, ISIN, enriched details). */
  readonly identifiers: ReadonlyArray<string | null | undefined>;
}

/**
 * Collects the source assets the operations need from accounts outside a given
 * perimeter.
 *
 * Transfers and sales can originate in an account the client has aggregated but
 * that is not part of the advisory proposal. The target-portfolio simulation only
 * applies an operation when its source position is present in `current_portfolio`,
 * so those positions have to be added to it, without pulling the whole account in.
 *
 * Cash legs (the origin of a buy) are ignored: cash is contributed by the accounts
 * of the perimeter.
 *
 * @param operations - Proposal operations to inspect.
 * @param scopedIbans - IBANs already covered by the perimeter.
 * @returns The out-of-perimeter source assets, grouped by IBAN.
 */
export const buildOperationSourceKeys = (
  operations: readonly InternalProposalOperation[],
  scopedIbans: Iterable<string>,
): OperationSourceKeys => {
  const scoped = new Set(scopedIbans);
  const keys = new Map<string, OperationSourceAsset[]>();

  operations.forEach(operation => {
    const iban = operation.from_iban;

    if (
      !iban ||
      scoped.has(iban) ||
      PLACEHOLDER_IBANS.has(iban) ||
      operation.operation_type === 'buy'
    ) {
      return;
    }

    const identifiers = [operation.from_ticker, operation.from_isin].filter(
      (identifier): identifier is string =>
        !!identifier && !isCashIdentifier(identifier),
    );

    if (identifiers.length === 0) {
      return;
    }

    const assets = keys.get(iban) ?? [];

    const isAlreadyCollected = assets.some(asset =>
      identifiers.some(identifier => asset.identifiers.has(identifier)),
    );

    if (isAlreadyCollected) {
      return;
    }

    assets.push({
      ticker: operation.from_ticker ?? operation.from_isin ?? '',
      isin: operation.from_isin ?? null,
      identifiers: new Set(identifiers),
    });

    keys.set(iban, assets);
  });

  return keys;
};

/** IBANs outside the perimeter that hold a source asset of an operation. */
export const operationSourceIbans = (keys: OperationSourceKeys): string[] =>
  Array.from(keys.keys());

/**
 * The source asset a position stands for, or `undefined` when no operation takes
 * that position out of that account.
 */
export const findOperationSourceAsset = (
  keys: OperationSourceKeys,
  position: OperationSourceCandidate,
): OperationSourceAsset | undefined => {
  const assets = position.iban ? keys.get(position.iban) : undefined;

  if (!assets) {
    return undefined;
  }

  return assets.find(asset =>
    position.identifiers.some(
      identifier => !!identifier && asset.identifiers.has(identifier),
    ),
  );
};

/** Whether a position is a source position an operation needs. */
export const isOperationSourcePosition = (
  keys: OperationSourceKeys,
  position: OperationSourceCandidate,
): boolean => !!findOperationSourceAsset(keys, position);
